import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Divider,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useParams, Link as RouterLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Helmet } from "react-helmet";
import blogPosts from "../data/blogPosts";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ReactMarkdown from "react-markdown"; // Uncomment if you use Markdown

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = blogPosts[slug];

  if (!post) {
    return (
      <>
        <Navbar />
        <Container sx={{ pt: 12, textAlign: "center" }}>
          <Typography variant="h4" fontWeight={700}>
            Post not found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            The blog post you’re looking for doesn’t exist or has been moved.
          </Typography>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | SortlyBooks Blog</title>
        <meta
          name="description"
          content={post.content.replace(/\n/g, " ").substring(0, 150) + "..."}
        />
        <meta property="og:title" content={post.title} />
        <meta
          property="og:description"
          content={post.content.replace(/\n/g, " ").substring(0, 150) + "..."}
        />
        <meta
          property="og:url"
          content={`https://www.sortlybooks.com/blog/${slug}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Navbar />

      <Box
        sx={{
          background: "linear-gradient(120deg,#f8fafc 0%,#e9f0fd 100%)",
          py: { xs: 7, md: 10 },
          minHeight: "100vh",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            minHeight: "100vh",
            px: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 820, // Wider for desktop
              borderRadius: { xs: "2rem", md: "1.5rem" }, // Softer corners
              p: { xs: 3, md: 6 },
              boxShadow:
                "0 4px 24px 0 rgba(30,32,78,0.08), 0 1.5px 6px 0 rgba(110,125,245,0.04)",
              border: "1.5px solid #ecf1fa",
              mt: { xs: 1, md: 6 },
              mb: { xs: 4, md: 8 },
            }}
          >
            {/* Back Button */}
            <Box sx={{ position: "absolute", left: 20, top: 20 }}>
              <Tooltip title="Back to Blog">
                <IconButton
                  component={RouterLink}
                  to="/blog"
                  size="small"
                  sx={{
                    bgcolor: "#f5f7fa",
                    "&:hover": { bgcolor: "#e3eaff" },
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Hero/Meta */}
            <Box sx={{ textAlign: "center", mt: 1 }}>
              {/* You can replace the emoji below with an SVG or hero if you wish */}
              <Box
                sx={{
                  width: 68,
                  height: 68,
                  borderRadius: "50%",
                  mx: "auto",
                  mb: 2,
                  background:
                    "linear-gradient(135deg,#eef3ff 60%,#dde7fa 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 34,
                  fontWeight: 600,
                  boxShadow: "0 2px 12px 0 rgba(100,130,255,0.07)",
                  letterSpacing: "-2px",
                }}
              >
                📄
              </Box>

              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Chip
                  label={new Date(post.date).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
                {/* Optional: Add more meta (author, tag, min read, etc) */}
              </Stack>
              <Typography
                variant="h3"
                fontWeight={800}
                sx={{
                  mb: 1,
                  fontSize: { xs: "1.7rem", md: "2.5rem" },
                  lineHeight: 1.2,
                  letterSpacing: "-1px",
                }}
              >
                {post.title}
              </Typography>
            </Box>
            <Divider sx={{ my: 3, opacity: 0.17 }} />

            {/* Post content - use Markdown if possible for best look */}
            <Box
              sx={{
                typography: "body1",
                color: "text.primary",
                lineHeight: 1.75,
                fontSize: "1.13rem",
                px: { xs: 0, md: 1 },
                "& strong": { fontWeight: 700 },
                "& em": { fontStyle: "italic" },
                "& a": {
                  color: "primary.main",
                  textDecoration: "underline",
                  "&:hover": { color: "primary.dark" },
                },
                "& p": { mb: 2 },
              }}
            >
              {/* <ReactMarkdown>{post.content}</ReactMarkdown> */}
              {post.content.split("\n").map((str, idx) =>
                str.trim() ? (
                  <Typography key={idx} paragraph sx={{ mb: 2 }}>
                    {str}
                  </Typography>
                ) : null
              )}
            </Box>

            <Divider sx={{ mt: 6, mb: 2, opacity: 0.13 }} />

            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                🚀 Enjoyed this article? Explore more on our{" "}
                <a href="/blog">SortlyBooks Blog</a>.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
